import React, { useState, useEffect } from 'react';
import { Button, useNotify, useRefresh, useRecordContext } from 'react-admin';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { gql } from 'apollo-boost';
import { client } from '../helpers/apolloClient';
import RoleMultiSelect from './roleMultiSelect';

// Role assignment is NOT a composed react-admin edit. `roles` was removed from
// every composed user update input so that this mutation is the only way to
// reach a role - a field that can bypass a hand-written mutation's invariant is
// an invariant that is not enforced. setUserRoles is what refuses to remove the
// last superAdmin and refuses to let a caller demote themselves, and those
// checks are only worth anything if there is no second way in.
//
// The errors this surfaces are deliberately specific and come straight from the
// server ("Cannot remove super-admin from your own account..."). They are shown
// rather than swallowed, because a refusal here means something about the state
// of the installation, not a typo.

const SET_USER_ROLES = gql`
    mutation setUserRoles($userId: MongoID!, $roles: [String!]!) {
        setUserRoles(userId: $userId, roles: $roles) {
            id
            roles
        }
    }
`;

const UserRoleEditor = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [roles, setRoles] = useState([]);

    // Re-seeded from the record rather than only on mount: the show page
    // refetches after a save, and without this the editor would keep offering
    // the roles it held before the refetch.
    //
    // Depending on `record` itself is correct here - react-admin's record comes
    // from the query cache and keeps its identity across local re-renders, so
    // typing in the editor does not reset it, while a refetch after save does.
    useEffect(() => {
        if (record && record.roles) {
            setRoles(record.roles);
        }
    }, [record]);

    if (!record) return null;

    const saved = JSON.stringify((record.roles || []).slice().sort())
        === JSON.stringify(roles.slice().sort());

    const handleSave = () => {
        client.mutate({
            mutation: SET_USER_ROLES,
            variables: { userId: record.id, roles },
        }).then(() => {
            notify('Roles updated');
            refresh();
        }).catch(e => {
            notify(e.message, { type: 'warning' });
        });
    };

    return (
        <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>Roles</Typography>
            <RoleMultiSelect
                value={roles}
                onChange={setRoles}
                label="Assigned roles"
                labelId={`roles-label-${record.id}`}
            />
            <Box mt={1}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    label="Save roles"
                    disabled={saved || roles.length === 0}
                />
            </Box>
            {!saved && roles.length === 0 && (
                <Typography variant="caption" color="error">
                    An account with no roles cannot do anything. Leave at least one selected.
                </Typography>
            )}
        </Box>
    );
};

export default UserRoleEditor;
